import React from 'react';
import './LegalPage.css';

const sections = [
  { id: 'acceptance', title: 'Acceptance of Terms' },
  { id: 'description', title: 'Description of Services' },
  { id: 'eligibility', title: 'Eligibility & Accounts' },
  { id: 'mobile-app-terms', title: 'Mobile Application & App Store Terms' },
  { id: 'acceptable-use', title: 'Acceptable Use' },
  { id: 'intellectual-property', title: 'Intellectual Property' },
  { id: 'user-content', title: 'User Content' },
  { id: 'payments', title: 'Fees, Payments & Refunds' },
  { id: 'third-party', title: 'Third-Party Links & Services' },
  { id: 'disclaimers', title: 'Disclaimers' },
  { id: 'limitation-of-liability', title: 'Limitation of Liability' },
  { id: 'indemnification', title: 'Indemnification' },
  { id: 'termination', title: 'Termination' },
  { id: 'governing-law', title: 'Governing Law & Disputes' },
  { id: 'changes', title: 'Changes to These Terms' },
  { id: 'contact', title: 'Contact Us' },
];

export default function TermsOfService() {
  return (
    <div className="legal-page">
      <section className="page-hero">
        <div className="container page-hero-content">
          <span className="section-label section-label--blue">Legal</span>
          <h1>Terms of <span style={{ color: '#d5772d' }}>Service</span></h1>
          <p>The terms and conditions governing your use of ThirdEye Cybersecurity's website, Learning Portal, and mobile applications.</p>
          <p className="legal-page-updated">Last updated: September 19, 2026</p>
        </div>
      </section>

      <section className="legal-section">
        <div className="container legal-layout">
          <nav className="legal-toc" aria-label="Table of contents">
            <h4>On This Page</h4>
            <ol>
              {sections.map(s => (
                <li key={s.id}><a href={`#${s.id}`}>{s.title}</a></li>
              ))}
            </ol>
          </nav>

          <div className="legal-content">
            <section id="acceptance">
              <h2><span className="legal-num">1.</span> Acceptance of Terms</h2>
              <p>
                These Terms of Service ("Terms") form a binding agreement between you and ThirdEye
                Cybersecurity ("ThirdEye," "we," "us," or "our") governing your access to and use of our
                website, Learning Portal, and any mobile application we publish, including applications
                distributed through the Google Play Store or Apple App Store (collectively, the
                "Services"). By accessing or using the Services, you agree to be bound by these Terms and
                our <a className="legal-link" href="/privacy-policy">Privacy Policy</a>. If you do not
                agree, you must not use the Services.
              </p>
            </section>

            <section id="description">
              <h2><span className="legal-num">2.</span> Description of Services</h2>
              <p>
                ThirdEye provides cybersecurity training and certification programs, staff augmentation,
                IT and security consulting, enterprise AI services, career and recruitment information,
                and related digital tools such as our Learning Portal and mobile application. We may add,
                modify, suspend, or discontinue any part of the Services at any time, with or without
                notice.
              </p>
            </section>

            <section id="eligibility">
              <h2><span className="legal-num">3.</span> Eligibility & Accounts</h2>
              <p>
                You must be at least 18 years old, or the age of legal majority in your jurisdiction, to
                enter into these Terms. If you create an account on our Learning Portal or mobile
                application, you agree to provide accurate information, keep your login credentials
                confidential, and notify us promptly of any unauthorized use of your account. You are
                responsible for all activity that occurs under your account.
              </p>
            </section>

            <section id="mobile-app-terms">
              <h2><span className="legal-num">4.</span> Mobile Application & App Store Terms</h2>
              <p>
                Where you download or use a ThirdEye mobile application, the following additional terms
                apply:
              </p>
              <ul>
                <li><strong>License:</strong> we grant you a limited, non-exclusive, non-transferable, revocable license to install and use the app on devices you own or control, solely for its intended personal or business purpose in connection with our Services.</li>
                <li><strong>Updates:</strong> we may issue updates, patches, or new versions of the app; some updates may be required to continue using the app.</li>
                <li><strong>Permissions:</strong> the app may request device permissions (such as storage or notifications) necessary to deliver certain features; you may manage these through your device settings.</li>
                <li><strong>App store terms:</strong> if you obtained the app from the Google Play Store, your use is also subject to Google Play's Terms of Service; if obtained from the Apple App Store, your use is also subject to Apple's Licensed Application End User License Agreement and Apple Media Services Terms. Google and Apple are third-party beneficiaries of these Terms solely to the extent required to enforce their respective policies and are not responsible for providing support or maintenance for the app.</li>
                <li><strong>Availability:</strong> app availability may vary by device, operating system version, and geographic region.</li>
              </ul>
            </section>

            <section id="acceptable-use">
              <h2><span className="legal-num">5.</span> Acceptable Use</h2>
              <p>When using the Services, you agree not to:</p>
              <ul>
                <li>Violate any applicable law, regulation, or third-party right.</li>
                <li>Reverse engineer, decompile, or attempt to extract the source code of our website, Learning Portal, or mobile application, except where permitted by law.</li>
                <li>Interfere with or disrupt the integrity or performance of the Services, including through malware, denial-of-service attacks, or unauthorized scraping.</li>
                <li>Attempt to gain unauthorized access to the Services, other accounts, or connected systems.</li>
                <li>Use the Services to transmit unlawful, infringing, defamatory, or harmful content.</li>
                <li>Misrepresent your identity or affiliation, or impersonate any person or entity.</li>
              </ul>
            </section>

            <section id="intellectual-property">
              <h2><span className="legal-num">6.</span> Intellectual Property</h2>
              <p>
                All content on the Services, including text, graphics, logos, course materials, software,
                and the ThirdEye name and branding, is owned by or licensed to ThirdEye and is protected
                by intellectual property laws. Except as expressly permitted under these Terms, you may
                not copy, modify, distribute, sell, or create derivative works from any part of the
                Services without our prior written consent.
              </p>
            </section>

            <section id="user-content">
              <h2><span className="legal-num">7.</span> User Content</h2>
              <p>
                If you submit content to us, such as messages, resumes, attachments, or forum/community
                posts within the Learning Portal or app, you retain ownership of that content but grant
                ThirdEye a non-exclusive, worldwide, royalty-free license to use, store, and process it as
                necessary to provide and improve the Services. You represent that you have the right to
                submit such content and that it does not infringe any third-party rights.
              </p>
            </section>

            <section id="payments">
              <h2><span className="legal-num">8.</span> Fees, Payments & Refunds</h2>
              <p>
                Fees for training programs, certifications, or services will be communicated to you at
                the time of enrollment or engagement. Payments are processed through the payment methods
                we make available and are subject to any additional terms disclosed at checkout or in
                your service agreement. Refunds, rescheduling, and cancellation terms for training
                programs will be provided at the time of enrollment; unless otherwise stated in writing,
                fees are non-refundable once a training program has commenced. Fees paid through a mobile
                app store are subject to that store's own payment and refund policies.
              </p>
            </section>

            <section id="third-party">
              <h2><span className="legal-num">9.</span> Third-Party Links & Services</h2>
              <p>
                The Services may link to or integrate with third-party websites, tools, or platforms
                (including our Learning Portal hosted on a separate domain). We do not control and are
                not responsible for the content, policies, or practices of third-party services. Your use
                of any third-party service is governed by that provider's own terms.
              </p>
            </section>

            <section id="disclaimers">
              <h2><span className="legal-num">10.</span> Disclaimers</h2>
              <p>
                The Services are provided "as is" and "as available" without warranties of any kind,
                whether express or implied, including warranties of merchantability, fitness for a
                particular purpose, non-infringement, or that the Services will be uninterrupted, secure,
                or error-free. Training outcomes, certification results, and staffing or job placement
                results are not guaranteed.
              </p>
            </section>

            <section id="limitation-of-liability">
              <h2><span className="legal-num">11.</span> Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, ThirdEye shall not be liable for any indirect,
                incidental, special, consequential, or punitive damages, or any loss of profits, data, or
                goodwill, arising from or related to your use of the Services. Our total liability for any
                claim arising out of or relating to these Terms or the Services shall not exceed the
                amount you paid to us, if any, in the twelve months preceding the claim.
              </p>
            </section>

            <section id="indemnification">
              <h2><span className="legal-num">12.</span> Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless ThirdEye, its officers, employees, and partners
                from any claims, damages, liabilities, and expenses (including reasonable legal fees)
                arising from your violation of these Terms, misuse of the Services, or infringement of
                any third-party right.
              </p>
            </section>

            <section id="termination">
              <h2><span className="legal-num">13.</span> Termination</h2>
              <p>
                We may suspend or terminate your access to the Services at any time, with or without
                notice, if we believe you have violated these Terms or for any other reason at our
                discretion. You may stop using the Services or delete your account at any time. Sections
                that by their nature should survive termination (including intellectual property,
                disclaimers, limitation of liability, and governing law) will continue to apply.
              </p>
            </section>

            <section id="governing-law">
              <h2><span className="legal-num">14.</span> Governing Law & Disputes</h2>
              <p>
                These Terms are governed by the laws of India, without regard to conflict-of-law
                principles. Subject to applicable law, any dispute arising out of or relating to these
                Terms or the Services shall be subject to the exclusive jurisdiction of the courts located
                in Ahmedabad, Gujarat, India.
              </p>
            </section>

            <section id="changes">
              <h2><span className="legal-num">15.</span> Changes to These Terms</h2>
              <p>
                We may revise these Terms from time to time. The updated Terms will be posted on this
                page with a revised "Last updated" date, and material changes will be communicated
                through the website, app, or by email where appropriate. Your continued use of the
                Services after changes take effect constitutes acceptance of the revised Terms.
              </p>
            </section>

            <section id="contact">
              <h2><span className="legal-num">16.</span> Contact Us</h2>
              <p>
                If you have questions about these Terms, please contact us at:
              </p>
              <div className="legal-contact-box">
                <strong>Shiddhipriya IT Consulting INC DBA ThirdEye Cyber Security</strong>
                4868 Wright Road,<br />
                Regina, S4W 0A8, Canada<br />
                Email: <a className="legal-link" href="mailto:info@thirdeyecybersecurity.com">info@thirdeyecybersecurity.com</a>
              </div>
            </section>
          </div>
        </div>
      </section>
    </div>
  );
}
